import { css, html, LitElement, nothing } from "lit";

import { ltErrorText } from "../lib/errors-lt";
import { controlStyles } from "../styles/tokens";

/**
 * <modulina-login> — pure login form: emits `sign-in`
 * (detail: { email, password }); the SHELL owns the /api/auth call
 * (components carry no transport knowledge — spec-web-ui §3). The uniform
 * `invalid_credentials` answer renders the same text for unknown email and
 * wrong password, exactly like the W1 page did.
 */
export class ModulinaLogin extends LitElement {
  static override properties = {
    busy: { type: Boolean, attribute: false },
    errorCode: { attribute: false },
  };

  declare busy: boolean;
  declare errorCode: string | null;

  constructor() {
    super();
    this.busy = false;
    this.errorCode = null;
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: block;
        max-width: 22rem;
        margin: 14vh auto 0;
        background: var(--m-color-surface);
        border: 1px solid var(--m-color-border);
        border-radius: var(--m-radius);
        box-shadow: var(--m-shadow);
        padding: var(--m-space-5);
      }
      h1 {
        font-size: var(--m-fs-title);
        margin: 0 0 var(--m-space-4);
      }
      .field {
        margin-bottom: var(--m-space-3);
      }
      .error {
        color: var(--m-color-danger);
        font-size: var(--m-fs-small);
        min-height: 1.2em;
        margin: var(--m-space-2) 0;
      }
      .error .code {
        opacity: 0.75;
      }
      button.primary {
        width: 100%;
        padding: var(--m-space-2);
      }
    `,
  ];

  override render() {
    return html`
      <h1>Modulina</h1>
      <form @submit=${this.onSubmit}>
        <div class="field">
          <label for="email">El. paštas</label>
          <input id="email" name="email" type="email" autocomplete="username" required />
        </div>
        <div class="field">
          <label for="password">Slaptažodis</label>
          <input
            id="password"
            name="password"
            type="password"
            autocomplete="current-password"
            required
          />
        </div>
        <div class="error" role="alert">
          ${this.errorCode
            ? html`${ltErrorText(this.errorCode)}
                <span class="code">(kodas: ${this.errorCode})</span>`
            : nothing}
        </div>
        <button class="primary" type="submit" ?disabled=${this.busy}>
          ${this.busy ? "Jungiamasi…" : "Prisijungti"}
        </button>
      </form>
    `;
  }

  private onSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    this.dispatchEvent(
      new CustomEvent("sign-in", {
        detail: {
          email: String(data.get("email") ?? ""),
          password: String(data.get("password") ?? ""),
        },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define("modulina-login", ModulinaLogin);
